import React, { useCallback, useImperativeHandle, useRef, useState } from "react";
import clsx from "clsx";
import { Alert, Box, Button, Form, Typography } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { formatCurrencyFn, isFunction, mergeQueryToUrl, refIsRequiredError } from "@util/utils";
import { EmployeeSelect } from "@icoach/components";
import { parseDate } from "@util/moment";
import useMessageDialog from "@util/hook/useMessageDialog";
import { BATCH_DEBIT_HISTORY, MAIN_TAB } from "@icoach/batchPayment/staticData";
import useOrderBatchPaymentApi from "@apis/useOrderBatchPaymentApi";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import { useHistory } from "react-router-dom";
import { ACCOUNT_BOOK_BULK_DEBIT_URL } from "@icoach/router/routerPath";

/** ================================
 * 📌 信用卡批次扣款
 *  ================================
 */
const BatchDeductionAction = React.forwardRef(({
												   searchParams,
												   sourceData,
												   refresh,
												   onHideAction,
												   getCheckedSourceData,
												   className
											   }, ref) => {
	const { type, date } = searchParams || {};
	const alertInvoiceWordSetting = sourceData?.alertInvoiceWordSetting || false;
	const history = useHistory();
	const { postOrderBatchPaymentConfirmApi } = useOrderBatchPaymentApi();
	const { employeeOption } = useGetEmployeeOptionState();
	const setMessageDialog = useMessageDialog();
	const [selectionStats, setSelectionStats] = useState({
		count: 0, // 批次扣款筆數
		totalAmount: 0 // 扣款總金額
	});

	const employeeRef = useRef(null);

	const handleRefresh = useCallback(() => {
			const today = new Date();
			const isCurrentMonth = new Date(date).getMonth() === today.getMonth();
			const updatedParams = {
				type,
				date,
				[MAIN_TAB]: BATCH_DEBIT_HISTORY
			};

			if (!isCurrentMonth) {
				setMessageDialog({
					open: true,
					title: "提示",
					msg: `扣款結果會顯示於${parseDate(today, "YYYY/MM")}的扣款結果列表中，請問要轉跳到該頁面嗎？`,
					buttonText: "確認轉跳",
					onConfirm: () => {
						updatedParams.date = parseDate(today, "YYYY-MM");
						redirectToTargetPage(updatedParams);
					}
				});
			} else {
				redirectToTargetPage(updatedParams);
			}
		},
		// eslint-disable-next-line
		[type, date]);

	const redirectToTargetPage = (params) => {
		const path = mergeQueryToUrl(ACCOUNT_BOOK_BULK_DEBIT_URL, params);
		history.push(path);
		if (isFunction(refresh)) {
			refresh(params);
		}
	};

	const getParams = () => {
		let params = {
			assignmentMonth: date,
			billingCategory: type
		};

		if (employeeRef.current && employeeRef.current.getResult) {
			Object.assign(params, { employeeID: employeeRef.current.getResult() });
		}

		if (isFunction(getCheckedSourceData)) {
			params.orderIDs = getCheckedSourceData().map((item) => item.orderID);
		}

		return params;
	};

	const doOrderBatchPaymentConfirmApi = async (params) => {
		const resp = await postOrderBatchPaymentConfirmApi(params);
		if (resp) {
			handleRefresh();
			isFunction(onHideAction) && onHideAction();
		}
	};

	const handleSubmit = () => {
		if (!refIsRequiredError(employeeRef)) {
			const params = getParams();
			if (params.orderIDs.length > 0) {
				doOrderBatchPaymentConfirmApi(params);
			} else {
				setMessageDialog({
					open: true,
					title: "提示",
					msg: "請選擇批次扣款的名單",
					buttonText: "確認"
				});
			}

		}
	};

	useImperativeHandle(ref, () => ({
			setSelectionStats: (params) => setSelectionStats(params)
		}),
		// eslint-disable-next-line
		[]);

	return (
		<Box className={clsx("mb-2 batch-controller-panel", className)}>
			<Form onSubmit={handleSubmit}>
				<Grid spacing={3} container>
					<Grid xs={7} item>
						<Typography className={"font-weight-bold"} variant={"body1"}>
							請選擇批次扣款的名單
						</Typography>
						<Typography variant={"body1"}>
							扣款筆數：<strong className={"h6 text-primary"}>{selectionStats.count}</strong>
						</Typography>
						<Typography className={"mb-0"} variant={"body1"}>
							扣款總金額：
							<strong
								className={"h6 text-primary"}>{formatCurrencyFn(selectionStats.totalAmount, { symbol: "$" })}</strong>
						</Typography>
					</Grid>
					<Grid xs={5} item>
						<Grid item xs={12}>
							<EmployeeSelect
								ref={employeeRef}
								options={employeeOption}
								label="擔當"
								className="mb-2"
								fullWidth
								required
							/>
							<Box className={"btn-group"}>
								<Button color={"grey"} variant={"outlined"} onClick={onHideAction}>
									取消
								</Button>
								<Button type={"submit"} variant={"outlined"}>
									進行批次扣款
								</Button>
							</Box>
						</Grid>
					</Grid>
					{alertInvoiceWordSetting && (
						<Grid xs={12} item>
							<Alert severity="error" variant="filled" color="error">
								當月發票字軌不足，請至綠界申請
							</Alert>
						</Grid>
					)}
				</Grid>
			</Form>
		</Box>
	);
});

export default BatchDeductionAction;
